<template>
  <div class="rechargeCenter">
    <div class="content">
      <div class="centerTabs">
        <van-tabs v-model="centerTab" color="#006EFF" title-active-color="#006EFF">
          <van-tab :title="$t('recharge_center.tab_recharge')" name="recharge" >

            <div v-show="centerTab === 'recharge'" class="summaryCard">

              <div class="summaryItem">
                <div class="label">{{ $t('recharge_center.summary_total_devices') }}</div>
                <div class="value">{{ authorizeNum }}</div>
              </div>
              <div class="summaryItem">
                <div class="label">{{ $t('recharge_center.expired_devices') }}</div>
                <div class="value danger">{{ overdueDevice }}</div>
              </div>
              <div class="summaryItem">
                <div class="label">{{ $t('recharge_center.expiring_within_30_days') }}</div>
                <div class="value danger">{{ valid30Count }}</div>
              </div>
            </div>

            <div v-show="centerTab === 'recharge'" class="section">
              <div class="section-title">{{ $t('recharge_center.select_plan') }}</div>
              <van-radio-group v-model="selectedPlanId" class="planGroup">
                <div class="planGrid">
                  <div
                      v-for="plan in plans"
                      :key="plan.id"
                      class="planCard"
                      :class="{ active: selectedPlanId === plan.id }"
                      @click="selectedPlanId = plan.id"
                  >
                    <div class="planTop">
                      <div class="planName">{{ getPlanName(plan) }}</div>

                    </div>
                    <div class="planPrice">
                      <span class="currency">¥</span>
                      <span class="amount">{{ plan.priceYuan }}</span>
                      <span class="unit">{{ $t('recharge_center.price_unit_per_device') }}</span>

                    </div>
                    <div class="planDesc">{{ getPlanDesc(plan) }}</div>
                    <van-radio :name="plan.id" class="planRadio" />
                  </div>
                </div>
              </van-radio-group>
            </div>

            <div v-show="centerTab === 'recharge'" class="section">
              <div class="section-title row">
                <span>{{ $t('recharge_center.select_device') }}</span>
                <span class="sub">{{ $t('recharge_center.device_selection_stats', { total: selectableTotal, selected: selectedDevices.length }) }}</span>
              </div>
              <div class="deviceQueryBlock">
                <div class="filterBar">
                  <van-search
                      v-model="keyword"
                      :placeholder="$t('recharge_center.search_device_name')"
                      shape="round"
                      @search="onSearch"
                      @clear="onSearch"
                  />
                </div>
                <div class="expireQueryBar" @click="showExpireTabSheet = true">
                  <div class="typeMenuTrigger">
                    <span class="typeMenuText">{{ expireTabLabel }}</span>
                    <van-icon name="arrow-down" class="typeMenuIcon" />
                  </div>
                </div>
              </div>
              <div class="selectAll" v-if="filteredAllDataList.length > 0">
                <van-checkbox
                    icon-size="22px"
                    v-model="isCheckAll"
                    label-position="left"
                    :indeterminate="isIndeterminate"
                    @change="checkAllChange"
                >
                  {{ $t('recharge_center.select_all') }}
                </van-checkbox>
              </div>

              <div class="list" ref="scrollContainer">
                <van-checkbox-group v-model="deviceSelect">
                  <van-list
                      v-model="loading"
                      :finished="finished"
                      :immediate-check="false"
                      :finished-text="finishedText"
                      @load="onLoad"
                  >
                    <div
                        v-for="(item) in pagedData"
                        :key="item.selectKey"
                        class="deviceRow"
                        @click="toggleCheckbox(item)"
                    >
                      <van-cell
                          class="deviceCell"
                          :icon="item.onlineState === '1' ? '/static/images/terminal/icon_dev_online.png' : '/static/images/terminal/icon_dev_disonline.png'"
                      >
                        <template #title>
                          <div class="deviceTitle">
                            <span class="deviceName">{{ item.name }}</span>
                            <span
                                class="deviceExpire"
                                :class="{
                          bad: item.expireStatus === 'expired',
                          warn: item.expireStatus === 'expiringSoon'
                        }"
                            >
                        {{ formatExpireText(item) }}
                      </span>
                          </div>
                        </template>
                      </van-cell>
                      <van-checkbox
                          :name="toKey(item)"
                          :checked="isChecked(item)"
                          :class="['deviceCheckbox', { 'custom-unchecked': !isChecked(item) }]"
                      />
                    </div>
                  </van-list>
                </van-checkbox-group>

                <van-empty
                    v-if="filteredAllDataList.length === 0"
                    class="empty"
                    image="/static/images/terminal/empty-img.png"
                    :description="$t('recharge_center.no_renewable_device')"
                />
              </div>
            </div>

          </van-tab>
          <van-tab :title="$t('recharge_center.tab_order')" name="order" >
            <div class="section orderSection">

              <div class="orderSummary">
                <span class="k">{{ $t('recharge_center.pending_invoice_amount') }}</span>
                <span class="v price">¥{{ notInvoiceAmount/100 }}</span>
              </div>
              <van-list
                  v-model="orderLoading"
                  :finished="orderFinished"
                  :finished-text="orderFinishedText"
                  :immediate-check="false"
                  @load="onLoadOrder"
              >
                <div v-for="order in orderList" :key="order.orderNo || order.id" class="orderCard">
                  <div class="orderRow">
                    <span class="k">{{ $t('recharge_center.product') }}</span>
                    <span class="v">{{ order.goodsName || '-' }}</span>
                  </div>
                  <div class="orderRow">
                    <span class="k">{{ $t('recharge_center.amount') }}</span>
                    <span class="v price">¥{{ order.amount }}</span>
                  </div>
                  <div class="orderRow">
                    <span class="k">{{ $t('recharge_center.status') }}</span>
                    <span class="v">{{ order.statusText || '-' }}</span>
                  </div>
                  <div class="orderRow">
                    <span class="k">{{ $t('recharge_center.time') }}</span>
                    <span class="v">{{ order.createTime || '-' }}</span>
                  </div>
                  <div class="orderActions">
                    <van-button
                      size="small"
                      plain
                      hairline
                      type="primary"
                      color="#006EFF"
                      @click="openOrderDetail(order)"
                    >
                      {{ $t('recharge_center.order_detail') }}
                    </van-button>
                    <van-button
                      size="small"
                      type="primary"
                      color="#006EFF"
                      :disabled="order.invoiceState !== 0"
                      @click="openInvoiceDialog(order)"
                    >
                      {{ $t('recharge_center.request_invoice') }}
                    </van-button>
                  </div>
                </div>
              </van-list>
              <van-empty
                  v-if="orderList.length === 0 && !orderLoading"
                  class="empty"
                  image="/static/images/terminal/empty-img.png"
                  :description="$t('recharge_center.no_order')"
              />
            </div>
          </van-tab>
          <van-tab :title="$t('recharge_center.tab_invoice_record')" name="invoice">
            <div class="section">
              <InvoiceCenter ref="invoiceCenterRef" />
            </div>
          </van-tab>
        </van-tabs>
      </div>

      <!-- 仅充值 Tab：放在 van-tabs 外，避免 tabs 内 transform 导致 iOS 上 fixed 失效；其它 Tab 不挂载不占位 -->
      <div v-if="centerTab === 'recharge'" class="footerBar">
        <div class="sum">
          <div class="sumLine">
            <span class="label">{{ $t('recharge_center.package_label') }}</span>
            <span class="value">{{ selectedPlan ? getPlanName(selectedPlan) : $t('recharge_center.unselected') }}</span>
          </div>
          <div class="sumLine">
            <span class="label">{{ $t('recharge_center.total_label') }}</span>
            <span class="value price">¥{{ totalPrice }}</span>
          </div>
        </div>
        <van-button
          color="#006EFF"
          class="payBtn"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ $t('recharge_center.tab_recharge') }}
        </van-button>
      </div>

      <div v-if="centerTab === 'recharge'" class="contentFooterPad" />
    </div>

    <van-overlay :show="showLoading">
      <div class="overlayWrap">
        <van-loading size="40px" vertical>{{ loadingText }}</van-loading>
      </div>
    </van-overlay>

    <van-popup
      v-model="payDialogVisible"
      position="bottom"
      :close-on-click-overlay="false"
      class="payPopup"
    >
      <div class="payHeader">
        <span class="payTitle">{{ $t('recharge_center.wechat_scan_pay') }}</span>
        <van-icon name="close" class="payClose" @click="closePayDialog(0)" />
      </div>
      <div class="payBody">
        <div v-if="payCountdown === '00:00'" class="payExpiredTip">{{ $t('recharge_center.qr_expired_tip') }}</div>
        <div class="payQrWrap" v-if="payQrUrl">
          <qrcode-vue
            :value="payQrUrl"
            :size="200"
            level="M"
            :class="{ expiredQr: payCountdown === '00:00' }"
          />
        </div>
        <van-field :label="$t('recharge_center.order_no')" :value="payOrderNo" readonly />
        <van-field :label="$t('recharge_center.remaining_time')" :value="payCountdown" readonly />
        <div class="payBtns">

          <van-button size="small" type="primary" color="#006EFF" @click="closePayDialog(1)">{{ $t('recharge_center.paid') }}</van-button>
        </div>
      </div>
    </van-popup>

    <van-popup
      v-model="orderDetailPopupVisible"
      position="bottom"
      round
      class="orderDetailPopup"
      @closed="orderDetailRecord = null"
    >
      <div class="orderDetailHeader">
        <span class="orderDetailTitle">{{ $t('recharge_center.order_detail') }}</span>
        <van-icon name="cross" class="orderDetailClose" @click="orderDetailPopupVisible = false" />
      </div>
      <div v-if="orderDetailRecord" class="orderDetailBody">
        <div class="orderDetailLine">
          <span class="orderDetailLabel">{{ $t('recharge_center.order_no') }}</span>
          <span class="orderDetailValue">{{ orderDetailRecord.orderNo || '-' }}</span>
        </div>

        <div class="orderDetailBlock">
          <div class="orderDetailLabel">{{ $t('recharge_center.detail_description') }}</div>
          <div class="orderDetailMultiline">{{ orderDetailRecord.detail || '-' }}</div>
        </div>
      </div>
    </van-popup>

    <van-dialog
      v-model="invoiceDialogVisible"
      :title="$t('recharge_center.request_invoice')"
      show-cancel-button
      :before-close="beforeInvoiceClose"
      class="invoiceDialog"
    >
      <van-form ref="invoiceFormRef" class="invoiceBody" :show-error-message="true">
        <van-field
            readonly
            clickable
            name="titleSelectedName"
            :value="invoiceForm.titleSelectedName"
            :label="$t('recharge_center.invoice_title_info')"
            :placeholder="$t('recharge_center.select_saved_invoice_title_optional')"
            @click="titlePickerShow = true"
        />
        <van-field
          v-model="invoiceForm.title"
          name="title"
          :label="$t('recharge_center.invoice_title')"
          :placeholder="$t('recharge_center.enter_or_select_invoice_title')"
          :rules="invoiceRules.title"
        />
        <div class="radioBlock">
          <div class="radioLabel">{{ $t('recharge_center.title_type') }}</div>
          <van-radio-group v-model="invoiceForm.titleType" direction="horizontal" class="radioGroup">
            <van-radio
              v-for="item in titleTypeActions"
              :key="item.value"
              :name="item.value"
            >
              {{ item.name }}
            </van-radio>
          </van-radio-group>
        </div>
        <div class="radioBlock">
          <div class="radioLabel">{{ $t('recharge_center.invoice_type') }}</div>
          <van-radio-group v-model="invoiceForm.invoiceType" direction="horizontal" class="radioGroup">
            <van-radio
              v-for="item in invoiceTypeActions"
              :key="item.value"
              :name="item.value"
            >
              {{ item.name }}
            </van-radio>
          </van-radio-group>
        </div>
        <van-field
          v-show="false"
          name="titleType"
          :value="String(invoiceForm.titleType)"
          :rules="invoiceRules.titleType"
        />
        <van-field
          v-show="false"
          name="invoiceType"
          :value="String(invoiceForm.invoiceType)"
          :rules="invoiceRules.invoiceType"
        />
        <van-field
          v-if="Number(invoiceForm.titleType) === 1"
          v-model="invoiceForm.taxNo"
          name="taxNo"
          :label="$t('recharge_center.tax_no')"
          :placeholder="$t('recharge_center.enter_tax_no')"
          :rules="invoiceRules.taxNo"
        />
        <van-field
          v-model="invoiceForm.phone"
          name="phone"
          :label="$t('append_field.phone_number')"
          type="tel"
          :placeholder="$t('recharge_center.optional_placeholder')"
        />
        <van-field
          v-model="invoiceForm.email"
          name="email"
          :label="$t('append_field.email')"
          type="email"
          :placeholder="$t('append_field.enter_email')"
          :rules="invoiceRules.email"
        />
      </van-form>
    </van-dialog>

    <van-action-sheet
      v-model="titlePickerShow"
      :actions="invoiceTitleActions"
      :cancel-text="$t('append_field.cancel')"
      close-on-click-action
      @select="onSelectInvoiceTitle"
    />

    <van-action-sheet
      v-model="showExpireTabSheet"
      :actions="expireTabActions"
      :cancel-text="$t('append_field.cancel')"
      close-on-click-action
      @select="onExpireTabSelect"
    />
  </div>
</template>

<script>
import { Toast, Dialog } from 'vant'
import qs from 'qs'
import QrcodeVue from 'qrcode.vue'
import InvoiceCenter from '@/views/InvoiceCenter.vue'



function parseDateMs(v) {
  if (!v) return NaN
  // 兼容 "yyyy-MM-dd" / "yyyy-MM-dd HH:mm:ss"
  const s = String(v).replace(/-/g, '/')
  const t = new Date(s).getTime()
  return t
}

function daysDiff(fromTs, toTs) {
  return Math.floor((toTs - fromTs) / (1000 * 60 * 60 * 24))
}

function startOfDayMs(ts) {
  const d = new Date(ts)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

function toYmd(date = new Date()) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function addDays(date, days) {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

export default {
  name: 'RechargeCenter',
  components: {
    QrcodeVue,
    InvoiceCenter
  },
  data() {
    return {
      showLoading: false,
      loadingText: '',
      centerTab: 'recharge',

      expireTab: 'expired',
      showExpireTabSheet: false,
      suppressExpireTabWatch: false,

      plans: [],
      selectedPlanId: '',

      // getClientRenewInfo.json 统计数据
      authorizeNum: 0, // 设备总数
      overdueDevice: 0, // 过期设备数
      valid30Count: 0, // 30天内到期设备数
      keyword: '',
      allDataList: [],
      pagedData: [],
      pageSize: 60,
      pageNo: 1,
      loading: false,
      finished: false,
      refreshing: false,
      total: 0,
      finishedText: '',
      fetching: false,

      deviceSelect: [],
      selectedDevices: [],
      isCheckAll: false,
      isIndeterminate: false,
      suppressCheckAllChange: false,

      // 支付弹窗
      payDialogVisible: false,
      payQrUrl: '',
      payExpireTime: 0,
      payOrderNo: '',
      payCountdown: '',
      payTimer: null,

      orderList: [],
      orderPageNo: 1,
      orderPageSize: 10,
      orderTotal: 0,
      orderLoading: false,
      orderFinished: false,
      orderFinishedText: '',
      orderAllList: [],
      notInvoiceAmount: '0.00',

      orderDetailPopupVisible: false,
      orderDetailRecord: null,

      invoiceDialogVisible: false,
      titlePickerShow: false,
      currentInvoiceOrder: null,
      invoiceTitleList: [],
      invoiceForm: {
        title: '',
        titleSelectedName: '',
        titleType: 0,
        invoiceType: 0,
        taxNo: '',
        phone: '',
        email: '',
      },
    }
  },
  computed: {
    expireTabOptions() {
      return [
        { text: this.$t('recharge_center.all_devices'), value: 'all' },
        { text: this.$t('recharge_center.expired_devices'), value: 'expired' },
        { text: this.$t('recharge_center.expiring_within_30_days'), value: 'expiringSoon' },
      ]
    },
    expireTabLabel() {
      const opt = this.expireTabOptions.find((o) => o.value === this.expireTab)
      return opt ? opt.text : this.$t('recharge_center.expired_devices')
    },
    expireTabActions() {
      return this.expireTabOptions.map((o) => ({
        name: o.text,
        value: o.value,
      }))
    },
    selectableTotal() {
      const t = Number(this.total || 0)
      return t > 0 ? t : this.allDataList.length
    },
    selectedPlan() {
      return this.plans.find((p) => p.id === this.selectedPlanId) || null
    },
    filteredAllDataList() {
      return this.allDataList
    },
    canSubmit() {
      return !!this.selectedPlanId && this.selectedDevices.length > 0
    },
    totalPrice() {
      const price = this.selectedPlan ? Number(this.selectedPlan.priceFen || 0) : 0
      const totalFen = price * this.selectedDevices.length
      return (totalFen / 100).toFixed(2)
    },
    invoiceRules() {
      return {
        title: [{ required: true, message: this.$t('recharge_center.fill_invoice_title') }],
        titleType: [{ required: true, message: this.$t('recharge_center.select_title_type') }],
        invoiceType: [{ required: true, message: this.$t('recharge_center.select_invoice_type') }],
        taxNo: [{
          validator: (val) => {
            if (Number(this.invoiceForm.titleType) === 1) {
              return !!String(val || '').trim()
            }
            return true
          },
          message: this.$t('recharge_center.enterprise_tax_no_required'),
        }],
        email: [
          { required: true, message: this.$t('append_field.enter_email') },
          {
            validator: (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val || '')),
            message: this.$t('recharge_center.invalid_email'),
          },
        ],
      }
    },
    titleTypeActions() {
      return [
        { name: this.$t('recharge_center.personal'), value: 0 },
        { name: this.$t('recharge_center.enterprise'), value: 1 },
      ]
    },
    invoiceTypeActions() {
      if (Number(this.invoiceForm.titleType) === 0) {
        return [
          { name: this.$t('recharge_center.ordinary_invoice'), value: 0 },
        ]
      }

      return [
        { name: this.$t('recharge_center.ordinary_invoice'), value: 0 },
        { name: this.$t('recharge_center.vat_invoice'), value: 1 },
      ]
    },
    invoiceTitleActions() {
      return this.invoiceTitleList.map((it) => ({
        name: it.invoiceTitle || it.title || '',
        value: it,
      }))
    },
    orderDetailIdText() {
      const r = this.orderDetailRecord
      if (!r) return '—'
      const id = r.id
      if (id === null || id === undefined || id === '') return '—'
      return String(id)
    },
  },
  watch: {
    expireTab() {
      if (this.suppressExpireTabWatch) return
      this.reloadDevices()
    },
    'invoiceForm.titleType'(v) {
      if (Number(v) === 0 && Number(this.invoiceForm.invoiceType) === 1) {
        this.invoiceForm.invoiceType = 0
      }
    },
    deviceSelect() {
      this.updateCheckAllState()
    },
    centerTab(v) {
      if (v === 'recharge') {
        this.resetRechargeTabState()
        this.init()
        return
      }
      if (v === 'order') {
        this.resetOrderTabState()
        this.resetAndLoadOrders()
        return
      }
      if (v === 'invoice') {
        this.resetInvoiceTabState()
        this.$nextTick(() => {
          if (this.$refs.invoiceCenterRef && this.$refs.invoiceCenterRef.reload) {
            this.$refs.invoiceCenterRef.reload()
          }
        })
      }
    },
  },
  async mounted() {
    document.title = this.$t('recharge_center.title')
    const tabbar = document.querySelector('.van-tabbar')
    if (tabbar) tabbar.style.display = 'none'

    await this.init()
  },
  beforeDestroy() {
    const tabbar = document.querySelector('.van-tabbar')
    if (tabbar) tabbar.style.display = ''
    this.clearPayCountdown()
  },
  methods: {
    resetRechargeTabState() {
      this.keyword = ''

      this.suppressExpireTabWatch = true
      this.expireTab = 'expired'
      this.$nextTick(() => {
        this.suppressExpireTabWatch = false
      })
      this.showExpireTabSheet = false

      this.deviceSelect = []
      this.selectedDevices = []
      this.suppressCheckAllChange = false
      this.isCheckAll = false
      this.isIndeterminate = false

      this.pageNo = 1
      this.total = 0
      this.allDataList = []
      this.pagedData = []
      this.loading = false
      this.finished = false
      this.refreshing = false
      this.finishedText = ''
      this.fetching = false

      this.payDialogVisible = false
      this.payQrUrl = ''
      this.payExpireTime = 0
      this.payOrderNo = ''
      this.payCountdown = ''
      this.clearPayCountdown()
    },
    resetOrderTabState() {
      this.orderDetailPopupVisible = false
      this.orderDetailRecord = null
      this.invoiceDialogVisible = false
      this.titlePickerShow = false
      this.currentInvoiceOrder = null
      this.invoiceTitleList = []
      this.invoiceForm = {
        title: '',
        titleSelectedName: '',
        titleType: 0,
        invoiceType: 0,
        taxNo: '',
        phone: '',
        email: '',
      }
    },
    resetInvoiceTabState() {
      this.orderDetailPopupVisible = false
      this.orderDetailRecord = null
      this.invoiceDialogVisible = false
      this.titlePickerShow = false
      this.currentInvoiceOrder = null
    },
    back() {
      this.$router.back()
    },
    buildDeviceKey(item, fallbackKey = '') {
      if (!item) return fallbackKey
      const clId =  String(item.clId).trim()
      const clNo = String(item.clNo).trim()

      if (clId && clNo) return `${clNo}-${clId}`
      if (clId) return `id-${clId}`
      if (clNo) return `no-${clNo}`
      return fallbackKey
    },
    toKey(item) {
      return item && item.selectKey ? item.selectKey : this.buildDeviceKey(item)
    },

    formatExpireText(item) {
      if (!item) return ''
      return item.clValidTime || ''
    },
    getPlanName(plan) {
      const years = plan && plan.years ? plan.years : 0
      return this.$t('recharge_center.plan_name_years', { years })
    },
    getPlanDesc(plan) {
      const years = plan && plan.years ? plan.years : 0
      return this.$t('recharge_center.plan_desc_valid_years', { years })
    },
    async init() {
      try {
        this.showLoading = true
        this.loadingText = this.$t('append_field.loadinga')

        // 1) 取套餐与续费统计
        const renewInfoRes = await this.$ajax.get('/etv/getClientRenewInfo.json')
        if (renewInfoRes && renewInfoRes.data && renewInfoRes.data.code === 0) {
          const data = renewInfoRes.data.data || {}
          this.authorizeNum = Number(data.count || 0)

          this.overdueDevice = Number(data.validCount || 0)
          this.valid30Count = Number(data.valid30Count || 0)

          const priceList = data.priceList
          this.plans = priceList.map((p) => {
            const years = parseInt(p.years, 10)
            const priceFen = parseInt(p.price, 10)
            const priceYuan = isNaN(priceFen) ? '0.00' : (priceFen / 100).toFixed(2)
            return {
              id: `y${years}`,
              years,
              priceFen,
              priceYuan,
            }
          })
          this.selectedPlanId = this.plans[0] ? this.plans[0].id : ''
        } else {
          Toast((renewInfoRes && renewInfoRes.data && renewInfoRes.data.msg) || this.$t('recharge_center.get_plan_failed'))
        }

        await this.reloadDevices()
      } finally {
        this.showLoading = false
      }
    },
    async onSearch() {
      await this.reloadDevices()
    },
    onExpireTabSelect(action) {
      const v = action && action.value
      if (v === undefined || v === null) return
      this.expireTab = v
    },
    async reloadDevices() {
      this.loadingText = this.$t('recharge_center.loading_devices')
      this.showLoading = true
      try {
        // 重置分页并拉取第一页
        this.pageNo = 1
        this.total = 0
        this.allDataList = []
        this.pagedData = []
        this.finished = false
        this.finishedText = ''
        this.loading = true
        await this.fetchNextPage(true)
      } finally {
        this.showLoading = false
      }
    },
    onLoad() {
      this.fetchNextPage(false)
    },
    onRefresh() {
      this.reloadDevices()
    },
    async fetchNextPage(isFirstPage) {
      if (this.fetching) return
      if (this.finished && !isFirstPage) return

      this.fetching = true
      this.loading = true
      try {
        const beforeLen = this.allDataList.length
        const reqPageNo = this.pageNo
        const today = new Date()
        // 过期：结束日期=今天；30天内到期：起始=今天，结束=今天+30天
        const startDate =  ''
        const endDate = this.expireTab === 'all'? "" :toYmd(addDays(today, 30))
        const payload = {
          searchParam: this.keyword,
          startDate,
          endDate,
          pageSize: this.pageSize,
          pageNo: this.pageNo,
        }
        const res = await this.$ajax.post('/etv/selectRenewClient.json', qs.stringify(payload))
        if (res && res.data && res.data.code === 0) {
          const d = res.data.data || {}
          const list = Array.isArray(d.list) ? d.list : []
          const today0 = startOfDayMs(Date.now())

          const mapped = list.map((it, index) => {
            const expireAtMsRaw = parseDateMs(it.clValidTime)
            const expireAt0 = isNaN(expireAtMsRaw) ? NaN : startOfDayMs(expireAtMsRaw)
            const diff = isNaN(expireAt0) ? 0 : daysDiff(today0, expireAt0)
            let expireStatus = 'other'
            if (diff < 0) expireStatus = 'expired'
            else if (diff <= 30) expireStatus = 'expiringSoon'
            const selectKey = this.buildDeviceKey(it, `page-${reqPageNo}-idx-${index}`)
            return {
              clId: it.clId,
              clNo: it.clNo,
              name: it.clName,
              onlineState: it.onlineState || '-1',
              clValidTime: it.clValidTime,
              expireStatus,
              selectKey,
            }
          })

          const existed = new Set(this.allDataList.map((x) => this.toKey(x)))
          let addedCount = 0
          mapped.forEach((x) => {
            const key = this.toKey(x)
            if (!key || !existed.has(key)) {
              this.allDataList.push(x)
              if (key) existed.add(key)
              addedCount += 1
            }
          })
          this.pagedData = this.allDataList

          this.total = Number(d.total || this.total || 0)

          // 结束条件（防止最后一页/重复页无限触发）
          const serverPageNum = Number(d.pageNum || d.pageNo || this.pageNo || 1)
          const noMoreByTotal = this.total > 0 && this.allDataList.length >= this.total
          // 避免中间页“短页”导致提前结束：仅当 total 未知或已达到 total，才把短页当作结束
          const noMoreByShortPage =
            mapped.length > 0 && mapped.length < this.pageSize && (this.total === 0 || noMoreByTotal)
          const noMoreByEmpty = mapped.length === 0
          const noMoreByNoAppend = addedCount === 0 || this.allDataList.length === beforeLen
          const noMoreBySamePage = serverPageNum === this.pageNo && (noMoreByEmpty || noMoreByNoAppend || noMoreByShortPage || noMoreByTotal)

          this.finished = noMoreByTotal || noMoreByShortPage || noMoreByEmpty || noMoreBySamePage
          this.finishedText = this.finished ? this.$t('append_field.noMore') : ''



          if (!this.finished) {
            // 以服务端回传页码为准，避免本地 pageNo 无限增长
            this.pageNo = serverPageNum + 1
          }
        } else {
          this.finished = true
          this.finishedText = this.$t('append_field.noMore')
        }

        this.updateCheckAllState()
      } catch (e) {
        this.finished = true
        this.finishedText = this.$t('append_field.noMore')
        Toast((e && e.message) )
      } finally {
        this.fetching = false
        this.loading = false
        this.refreshing = false
      }
    },
    isChecked(item) {
      return this.deviceSelect.includes(this.toKey(item))
    },
    toggleCheckbox(item) {
      const key = this.toKey(item)
      const idx = this.deviceSelect.indexOf(key)
      if (idx > -1) {
        this.deviceSelect.splice(idx, 1)
        this.selectedDevices = this.selectedDevices.filter((d) => this.toKey(d) !== key)
      } else {
        this.deviceSelect.push(key)
        if (!this.selectedDevices.some((d) => this.toKey(d) === key)) {
          this.selectedDevices.push(item)
        }
      }
      this.updateCheckAllState()
    },
    checkAllChange(checked) {
      // 由内部状态同步触发时，不执行“全选/清空”逻辑，避免取消一个后把全部清空
      if (this.suppressCheckAllChange) return

      if (checked) {
        const keys = this.allDataList.map((d) => this.toKey(d))
        this.deviceSelect = Array.from(new Set([...this.deviceSelect, ...keys]))
        const merged = [...this.selectedDevices]
        this.allDataList.forEach((d) => {
          const k = this.toKey(d)
          if (k && !merged.some((x) => this.toKey(x) === k)) merged.push(d)
        })
        this.selectedDevices = merged
      } else {
        const keys = new Set(this.allDataList.map((d) => this.toKey(d)))
        this.deviceSelect = this.deviceSelect.filter((k) => !keys.has(k))
        this.selectedDevices = this.selectedDevices.filter((d) => !keys.has(this.toKey(d)))
      }
      this.updateCheckAllState()
    },
    updateCheckAllState() {
      const total = this.allDataList.length
      if (total === 0) {
        this.suppressCheckAllChange = true
        this.isCheckAll = false
        this.isIndeterminate = false
        this.$nextTick(() => {
          this.suppressCheckAllChange = false
        })
        return
      }
      const keys = new Set(this.allDataList.map((d) => this.toKey(d)))
      const checkedCount = this.deviceSelect.filter((k) => keys.has(k)).length
      this.suppressCheckAllChange = true
      this.isCheckAll = checkedCount === total
      this.isIndeterminate = checkedCount > 0 && checkedCount < total
      this.$nextTick(() => {
        this.suppressCheckAllChange = false
      })
    },
    async submit() {
      if (!this.canSubmit) {
        Toast(this.$t('recharge_center.select_plan_and_device'))
        return
      }

      const plan = this.selectedPlan
      const count = this.selectedDevices.length
      const msg = [
        `${this.$t('recharge_center.package_label')}：${this.getPlanName(plan)}`,
        `${this.$t('recharge_center.device_count')}：${count}`,
        `${this.$t('recharge_center.total_label')}：¥${this.totalPrice}`,
        '',
        this.$t('recharge_center.confirm_create_payment_order'),
      ].join('\n')

      try {
        await Dialog.confirm({ title: this.$t('recharge_center.confirm_recharge'), message: msg })
      } catch (e) {
        return
      }

      this.showLoading = true
      this.loadingText = this.$t('recharge_center.submitting')
      try {
        const res = await this.$ajax.post(
          '/etv/createClientRenewPayment.json',
          qs.stringify({
            clIdList: this.selectedDevices.map((d) => d.clId).toString(),
            renewYears: plan.years,
          })
        )
        if (res && res.data && res.data.code === 0) {
          const data = res.data.data || {}
          const codeUrl = data.codeUrl || ''
          this.payQrUrl = codeUrl
          this.payExpireTime = data.expireTime || 0
          this.payOrderNo = data.tradeNo || ''
          this.payDialogVisible = true
          this.startPayCountdown()
        } else {
          Toast((res && res.data && res.data.msg) || this.$t('recharge_center.create_order_failed'))
        }
      } finally {
        this.showLoading = false
      }
    },

    async copyPayUrl() {

      window.open(this.payQrUrl, '_blank')
     /* const text = this.payQrUrl || ''
      if (!text) {
        Toast('暂无链接')
        return
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          Toast('已复制')
          return
        }
      } catch (e) {
        // ignore
      }
      // fallback
      const input = document.createElement('textarea')
      input.value = text
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        Toast('已复制')
      } catch (e) {
        Toast('复制失败，请手动复制')
      } finally {
        document.body.removeChild(input)
      }*/
    },
    isWeChatEnv() {
      const ua = (navigator.userAgent || '').toLowerCase()
      return /micromessenger/.test(ua)
    },


    closePayDialog(type) {
      this.payDialogVisible = false
      this.clearPayCountdown()
      this.payQrUrl = ''
      this.payExpireTime = 0
      this.payOrderNo = ''
      this.payCountdown = ''

      // type=1：已支付（这里先按PC逻辑清空选择）
      if (type === 1) {
        Toast(this.$t('recharge_center.payment_marked'))
        this.deviceSelect = []
        this.selectedDevices = []
        this.updateCheckAllState()
      }
    },
    startPayCountdown() {
      this.clearPayCountdown()
      this.updatePayCountdown()
      this.payTimer = setInterval(this.updatePayCountdown, 1000)
    },
    updatePayCountdown() {
      const now = Date.now()
      const remain = Math.floor((Number(this.payExpireTime) - now) / 1000)
      if (remain <= 0) {
        this.payCountdown = '00:00'
        this.clearPayCountdown()
      } else {
        const mm = String(Math.floor(remain / 60)).padStart(2, '0')
        const ss = String(remain % 60).padStart(2, '0')
        this.payCountdown = `${mm}:${ss}`
      }
    },
    clearPayCountdown() {
      if (this.payTimer) {
        clearInterval(this.payTimer)
        this.payTimer = null
      }
    },
    onLoadOrder() {
        this.fetchOrderList()
    },
    resetAndLoadOrders() {
      this.orderPageNo = 1
      this.orderTotal = 0
      this.orderFinished = false
      this.orderFinishedText = ''
      this.orderList = []
      this.orderAllList = []
      this.notInvoiceAmount = '0.00'
      this.fetchOrderList()
    },
    async fetchOrderList() {
      if (this.orderLoading || this.orderFinished) return
      this.orderLoading = true
      try {
        const res = await this.$ajax.get('/etv/getInvoiceOrderList.json')
        if (res && res.data && res.data.code === 0) {
          const data = res.data.data || {}
          const list = Array.isArray(data.invoicePaymentOrderList)
            ? data.invoicePaymentOrderList
            : (Array.isArray(data.list) ? data.list : [])

          this.notInvoiceAmount = Number(data.notInvoiceAmount || 0).toFixed(2)
          this.orderAllList = list.map((it) => ({
            id: it.id,
            goodsName: it.goodsName || '',
            // 后端返回 amount 单位为分，这里统一转为元显示
            amount: (Number(it.amount || 0) / 100).toFixed(2),
            quantity: it.quantity || 0,
            userName: it.userName || '',
            orderNo: it.orderNo || '',
            invoiceState: Number(it.invoiceState || 0),
            statusText: Number(it.invoiceState || 0) === 0
              ? this.$t('recharge_center.invoice_available')
              : this.$t('recharge_center.invoiced'),
            detail: it.detail || '',
            createTime: it.createTime || '',
            raw: it,
          }))

          this.orderTotal = this.orderAllList.length
          const start = (this.orderPageNo - 1) * this.orderPageSize
          const end = start + this.orderPageSize
          const pageChunk = this.orderAllList.slice(start, end)
          this.orderList = this.orderList.concat(pageChunk)

          if (this.orderList.length >= this.orderTotal || pageChunk.length === 0) {
            this.orderFinished = true
            this.orderFinishedText = this.$t('append_field.noMore')
          } else {
            this.orderPageNo += 1
          }
        } else {
          this.orderFinished = true
          this.orderFinishedText = this.$t('append_field.noMore')
        }
      } catch (e) {
        this.orderFinished = true
        this.orderFinishedText = this.$t('append_field.noMore')
        Toast((e && e.message) || this.$t('recharge_center.order_load_failed'))
      } finally {
        this.orderLoading = false
      }
    },
    openOrderDetail(order) {
      this.orderDetailRecord = order || null
      this.orderDetailPopupVisible = true
    },
    async openInvoiceDialog(order) {
      this.currentInvoiceOrder = order
      this.invoiceForm = {
        title: '',
        titleSelectedName: '',
        titleType: 0,
        invoiceType: 0,
        taxNo: '',
        phone: '',
        email: '',
      }
      await this.fetchInvoiceTitleList()
      this.invoiceDialogVisible = true
    },
    async fetchInvoiceTitleList() {
      try {
        const res = await this.$ajax.get('/etv/selectUserInvoiceList.json')
        if (res && res.data && res.data.code === 0) {
          const list =  res.data.data
          this.invoiceTitleList = list
        } else {
          this.invoiceTitleList = []
        }
      } catch (e) {
        this.invoiceTitleList = []
      }
    },
    onSelectInvoiceTitle(action) {
      const item = action && action.value ? action.value : null
      if (!item) return
      this.invoiceForm.title = item.invoiceTitle
      this.invoiceForm.titleSelectedName = item.invoiceTitle

      this.invoiceForm.titleType = Number(item.purchaserIdType )
      this.invoiceForm.invoiceType = 0
      this.invoiceForm.taxNo = item.taxNo
      this.invoiceForm.phone = item.phone
      this.invoiceForm.email = item.email

    },
    async beforeInvoiceClose(action, done) {
      if (action !== 'confirm') {
        done()
        return
      }
      const ok = await this.submitInvoiceApply()
      done(ok)
    },
    async submitInvoiceApply() {
      const f = this.invoiceForm
      try {
        await this.$refs.invoiceFormRef.validate()
      } catch (e) {
        return false
      }
      try {
        const payload = {
          orderIdList:[this.currentInvoiceOrder.id],
          invoiceTitle: f.title,
          purchaserIdType: Number(f.titleType),
          invoiceType: f.invoiceType,
          taxNo: f.taxNo,
          phone: f.phone,
          email: f.email,
        }
        const res = await this.$ajax.post('/etv/addInvoiceRecord.json', payload)
        if (res.data.code === 0) {
          Toast(this.$t('recharge_center.invoice_submitted'))
          await this.fetchInvoiceTitleList()
          return true
        }
        Toast(res.data.msg)
        return false
      } catch (e) {
        Toast((e && e.message) )
        return false
      }
    },
  },
}
</script>

<style lang="scss">
@import "../../public/static/css/app";

.rechargeCenter {
  height: 100% !important;
  overflow: hidden;
  background: #f6f7fb;


  .content {
    height: 100%;
    overflow: hidden;
    padding-bottom: 0;

    .van-notice-bar {
      margin: 10px 10px 0 10px;
      border-radius: 8px;
    }
    .centerTabs {
      background: #fff;
      border-radius: 8px;
      overflow: hidden;
      height: 100%;
      .van-tab__text--ellipsis{
        font-size: 18px;

      }
    }
    .centerTabs .van-tabs {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    .centerTabs .van-tabs__content {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 80px;
    }
    .centerTabs .van-tab__pane {
      min-height: 100%;
    }

    .summaryCard {
      margin: 8px 10px 0 10px;
      background: #fff;
      border-radius: 10px;
      overflow: hidden;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 0;
      border: 1px solid #f0f0f0;
    }

    .summaryItem {
      padding: 12px 10px;
      text-align: center;
      position: relative;

      &:not(:last-child)::after {
        content: '';
        position: absolute;
        top: 12px;
        right: 0;
        width: 1px;
        height: calc(100% - 24px);
        background: #eeeeee;
      }

      .label {
        font-size: 15px;
        color: #8a8a8a;
      }

      .value {
        margin-top: 6px;
        font-size: 18px;
        font-weight: 700;
        color: #111;
      }

      .danger {
        color: #ef4444;
      }
    }

    .section {
      background: #fff;
      border-radius: 10px;
      overflow: hidden;
      padding: 12px;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #111;
      margin-bottom: 10px;
      &.row {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .sub {
        font-size: 13px;
        color: #8a8a8a;
        font-weight: 400;
      }
    }

    .planGrid {
      display: grid;
      //grid-template-columns: 1fr 1fr;
      gap: 10px;
    }

    .planCard {
      border: 1px solid #e9e9ee;
      border-radius: 10px;
      padding: 10px 10px 8px 10px;
      position: relative;
      background: #fff;
      min-height: 92px;

      &.active {
        border-color: #006eff;
        box-shadow: 0 0 0 2px rgba(0, 110, 255, 0.08) inset;
      }

      .planTop {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }

      .planName {
        font-size: 15px;
        font-weight: 600;
        color: #111;
      }

      .planTag {
        font-size: 12px;
        color: #006eff;
        background: rgba(0, 110, 255, 0.1);
        padding: 2px 6px;
        border-radius: 999px;
        white-space: nowrap;
      }

      .planPrice {
        margin: 8px 0;
        display: flex;
        align-items: baseline;
        gap: 2px;
        color: #006eff;

        .currency {
          font-size: 12px;
          font-weight: 600;
        }
        .amount {
          font-size: 22px;
          font-weight: 700;
        }
        .unit {
          font-size: 12px;
          color: #7a7a7a;
          margin-left: 2px;
        }
      }

      .planDesc {
        margin: 8px 0;
        font-size: 12px;
        color: #8a8a8a;
      }

      .planRadio {
        position: absolute;
        right: 8px;
        bottom: 45px;
      }
    }

    .deviceQueryBlock {
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .expireQueryBar {
      flex-shrink: 0;

      .typeMenuTrigger {
        height: 36px;
        padding: 0 10px 0 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 4px;
        background: #f7f8fa;
        border-radius: 8px;
        min-width: 72px;
        max-width: 120px;
        box-sizing: border-box;
      }

      .typeMenuText {
        font-size: 13px;
        color: #323233;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .typeMenuIcon {
        flex-shrink: 0;
        font-size: 14px;
        color: #969799;
      }
    }
    .filterBar {
      flex: 1;
      min-width: 0;
      margin-bottom: 0;
      .van-search {
        padding: 0;
      }
    }

    .groupBar {
      display: flex;
      gap: 10px;
      overflow-x: auto;
      padding-bottom: 6px;
      margin-bottom: 6px;
      -webkit-overflow-scrolling: touch;
    }

    .groupBtn {
      flex: 0 0 auto;
      background: #fff;
      color: #111;
      border: 1px solid #e5e5e5;
    }

    .groupBtn.selected {
      background: #006eff;
      color: #fff;
      border-color: #006eff;
    }

    .selectAll {
      margin: 6px 0 8px 0;
      .van-checkbox__icon .van-icon {
        border: 1px solid #111;
      }
      .van-checkbox{
        display: flex;
        justify-content: space-between;
      }
    }

    .list {
      max-height: 60vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      box-sizing: border-box;
      /* 给 van-list 底部「没有更多了」留出可滚入视区的空间，避免贴底被裁切或难以辨认 */
      padding-bottom: 48px;
      ::v-deep .van-list__finished-text,
      ::v-deep .van-list__loading {
        padding: 14px 12px 20px;
        line-height: 22px;
        color: #969799;
        font-size: 14px;
        text-align: center;
      }

    }

    .deviceRow {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 0;
    }

    .deviceCell {
      flex: 1;
      padding: 10px 0;
      .van-cell__left-icon {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .van-icon__image {
        width: 36px;
        height: 36px;
      }
    }

    .deviceTitle {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
      width: 100%;
    }

    .deviceName {
      display: block;
      max-width: 200px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #111;
      font-size: 15px;
    }
    .deviceExpire {
      font-size: 15px;
      color: #006eff;
      background: rgba(0, 110, 255, 0.12);
      padding: 2px 6px;
      border-radius: 999px;
      white-space: nowrap;
      &.warn {
        color: #f59e0b;
        background: rgba(245, 158, 11, 0.12);
      }
      &.bad {
        color: #ef4444;
        background: rgba(239, 68, 68, 0.12);
      }
    }

    .deviceCheckbox {
      margin-left: 10px;
    }

    .custom-unchecked .van-checkbox__icon .van-icon {
      background-color: #fff !important;
      border-color: #111 !important;
    }

    .custom-unchecked .van-checkbox__icon .van-icon::after {
      border-color: transparent !important;
    }

    .empty {
      margin-top: 10px;
    }
    .orderSection {
      .orderSummary {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        padding: 8px 10px;
        background: #f8fafc;

      }
      .orderCard {
        border: 1px solid #ececec;
        border-radius: 10px;
        padding: 10px;
        margin-bottom: 10px;
      }
      .orderRow {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
      }
      .k {
        color: #7a7a7a;
      }
      .v {
        color: #111;
        max-width: 210px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .detail {
        max-width: 210px;
        white-space: normal;
        text-align: right;
        line-height: 18px;
      }
      .price {
        color: #ef4444;
        font-weight: 600;
      }
      .orderActions {
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
      }
    }
  }

  .footerBar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: 70px;
    background: #fff;
    border-top: 1px solid #eaeaea;
    display: flex;
    align-items: center;
    padding: 10px;
    gap: 10px;
    box-sizing: border-box;
    z-index: 100;

    .sum {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
      .sumLine {
        display: flex;
        justify-content: space-between;
        font-size: 13px;
        color: #666;
      }
      .price {
        color: #006eff;
        font-weight: 700;
        font-size: 16px;
      }
    }

    .payBtn {
      width: 120px;
      border-radius: 10px;
    }
  }

  .overlayWrap {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .payPopup {
    width: 100%;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    overflow: hidden;
    background: #fff;
  }

  .orderDetailPopup {
    width: 100%;
    background: #fff;
  }
  .orderDetailHeader {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background: #f6f7fb;
    border-bottom: 1px solid #eee;
  }
  .orderDetailTitle {
    font-size: 16px;
    font-weight: 600;
    color: #111;
  }
  .orderDetailClose {
    position: absolute;
    right: 12px;
    font-size: 18px;
    color: #666;
    padding: 8px;
  }
  .orderDetailBody {
    padding: 14px 16px 20px;
    max-height: 62vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
  }
  .orderDetailLine {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
    font-size: 14px;
  }
  .orderDetailLabel {
    flex: 0 0 auto;
    color: #7a7a7a;
    min-width: 64px;
  }
  .orderDetailValue {
    flex: 1;
    text-align: right;
    color: #111;
    word-break: break-all;
  }
  .orderDetailBlock {
    margin-top: 4px;
  }
  .orderDetailBlock .orderDetailLabel {
    display: block;
    margin-bottom: 8px;
  }
  .orderDetailMultiline {

    color: #333;
    line-height: 22px;
    white-space: pre-wrap;
    word-break: break-word;
    padding: 10px 12px;
    background: #f8fafc;
    border-radius: 8px;
  }

  .payHeader {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background: #f6f7fb;
  }
  .payTitle {
    font-size: 16px;
    font-weight: 600;
    color: #111;
  }
  .payClose {
    position: absolute;
    right: 12px;
    font-size: 18px;
    color: #666;
  }
  .payBody {
    padding: 12px;
  }
  .payQrWrap {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px 0 12px;
  }
  .payExpiredTip {
    text-align: center;
    color: #ef4444;
    font-size: 14px;
    margin-bottom: 8px;
    font-weight: 600;
  }
  .expiredQr {
    opacity: 0.45;
    filter: grayscale(100%);
  }
  .payBtns {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 10px;
  }
  .invoiceDialog {
    .invoiceBody {
      padding: 8px 0 2px;
      max-height: 58vh;
      overflow-y: auto;
    }
    .van-field__label {
      width: 74px;
    }
    .radioBlock {
      padding: 10px 16px 6px;
    }
    .radioLabel {

      color: #646566;
      margin-bottom: 8px;
    }
    .requiredStar {
      color: #ee0a24;
    }
    .radioGroup {
      display: flex;
      gap: 16px;
      .van-radio {
        margin-right: 16px;
      }
    }
  }

  [class*=van-hairline]::after {
    border: none;
  }
}
</style>

